import type { RefreshTokenFn } from "../../../shared/api/fetchWithRefresh";
import type { EmailContext } from "../../email/types";
import { htmlToText } from "../domain/agenda";
import { askCalendarAssistant } from "./agendaDraft";

/*
 * "Prepare for This Meeting", written by AiVA but kept to the mockup's shape:
 * the same few lines, each with its bold lead ("3 people.", "Read first:"),
 * one short sentence each. The pane works out the facts itself (who replied,
 * the files, the agenda, the meeting before) and AiVA only rewrites the text
 * after each lead with what the email thread adds. Whatever comes back is
 * checked against those leads, so the brief can't grow, lose a line or change
 * a fact's place; a line AiVA didn't return keeps the pane's own wording.
 */

/** One brief line: its bold lead, then the text. */
export interface BriefLine {
  readonly lead: string;
  readonly text: string;
}

/** A line longer than this is cut, so the brief stays as short as the mockup's. */
const LINE_TEXT_CAP = 140;

function briefRequest(subject: string, facts: readonly BriefLine[]): string {
  return [
    `Write a short brief for the "${subject}" meeting from this thread and the facts below.`,
    "Facts (all correct, keep every one):",
    ...facts.map((f) => `**${f.lead}** ${f.text}`),
    "Reply with exactly one line per fact, in the same order, and nothing else: no title, no intro, no extra lines.",
    "Start each line with its bold lead exactly as given, then one short sentence of at most 15 words.",
    "Add what the thread says where it helps, for example what a file is for or what the meeting has to decide, " +
      "but never change a name, number, time or reply status.",
    "Don't look up or change any meeting.",
  ].join("\n");
}

const clean = (s: string) =>
  s
    .replace(/\*\*|__/g, "")
    .replace(/^[\s:–—-]+/, "")
    .trim();

/**
 * The brief from AiVA's reply, one line per fact in the facts' order. A fact
 * AiVA didn't return keeps its own text. Null when nothing in the reply matches
 * a fact, so the caller can show the facts as they are.
 */
export function briefFromReply(reply: string, facts: readonly BriefLine[]): BriefLine[] | null {
  const text = /<\/?(p|ul|ol|li|br|div|strong|b)\b/i.test(reply) ? htmlToText(reply) : reply;
  const lines = text
    .split("\n")
    .map((line) => line.trim().replace(/^(?:[-*•]|\d+[.)])\s+/, ""))
    .filter(Boolean);
  let matched = 0;
  const out = facts.map((fact) => {
    const lead = fact.lead.toLowerCase();
    const line = lines.find((l) => clean(l).toLowerCase().startsWith(lead) || l.toLowerCase().startsWith(`**${lead}`));
    if (!line) return fact;
    const rest = clean(clean(line).slice(fact.lead.length));
    if (!rest) return fact;
    matched++;
    return { lead: fact.lead, text: rest.length > LINE_TEXT_CAP ? `${rest.slice(0, LINE_TEXT_CAP - 1).trimEnd()}…` : rest };
  });
  return matched > 0 ? out : null;
}

/** Ask AiVA to write the brief from `email` and the pane's own `facts`. Throws an Error that can be shown as it is. */
export async function writePrepBrief(options: {
  readonly apiToken: string;
  readonly refresh: RefreshTokenFn;
  readonly email: EmailContext;
  readonly subject: string;
  readonly facts: readonly BriefLine[];
  readonly userDisplayName: string;
  readonly signal: AbortSignal;
}): Promise<BriefLine[]> {
  const { reply } = await askCalendarAssistant({
    apiToken: options.apiToken,
    refresh: options.refresh,
    email: options.email,
    message: briefRequest(options.subject, options.facts),
    userDisplayName: options.userDisplayName,
    signal: options.signal,
    what: "write the brief",
  });
  const brief = briefFromReply(reply, options.facts);
  if (!brief) throw new Error("AiVA didn't return a brief in the expected shape.");
  return brief;
}
